'use client';

import { useLocale, useTranslations } from 'next-intl';
import { PropsWithChildren, useEffect, useState } from 'react';
import SuperTokens, { SuperTokensWrapper } from 'supertokens-auth-react';
import { EmailPasswordComponentsOverrideProvider } from 'supertokens-auth-react/recipe/emailpassword';
import { AuthRecipeComponentsOverrideContextProvider } from 'supertokens-auth-react/ui';

import { Image, Typography } from '@/components/atoms';
import { List } from '@/components/molecules';
import { useRouter, usePathname } from '@/i18n/routing';
import { initAuth, setRouter } from '@/lib/auth';
import { useAnalytics } from '@/lib/contexts/analytics';
import { logger } from '@/lib/logger';
import { getAllTranslations } from '@/lib/services/fetchTranslations';

import { tenantMap } from '../auth/constants';

interface SuperTokensProviderProps extends PropsWithChildren {
  config: Awaited<ReturnType<typeof import('@/lib/api').fetchSiteConfig>>;
}

export const SuperTokensProvider = ({
  children,
  config,
}: SuperTokensProviderProps) => {
  const { track, identify, reset } = useAnalytics();
  const [isSupertokenInitialized, setSupertokenInitialized] = useState(false);
  const locale = useLocale();
  const t = useTranslations();
  setRouter(useRouter(), usePathname() || window.location.pathname);

  useEffect(() => {
    const initialize = async () => {
      try {
        const translations = await getAllTranslations(config?.languages);
        SuperTokens.init(
          await initAuth(config, translations, { track, identify, reset }),
        );
        setSupertokenInitialized(true);
      } catch (error) {
        const typedError = error as Error & { digest?: string };
        logger.error(typedError.message, {
          source: 'nextjs-app-supertokens-provider',
          digest: typedError.digest,
          stack: typedError.stack,
        });
      }
    };

    if (!isSupertokenInitialized) {
      initialize();
    }
  }, [isSupertokenInitialized, config]);

  useEffect(() => {
    if (isSupertokenInitialized) {
      SuperTokens.changeLanguage(locale.toLowerCase());
    }
  }, [locale, isSupertokenInitialized]);

  useEffect(() => {
    type AccessibleElement = HTMLElement & {
      _accessibilityEnhanced?: boolean;
    };

    const enhanceAccessibility = () => {
      const links = document.querySelectorAll<HTMLElement>(
        '[data-supertokens~="link"]',
      );

      links.forEach(link => {
        const el = link as AccessibleElement;

        if (!el.hasAttribute('tabindex')) {
          el.tabIndex = 0;
        }

        if (!el.hasAttribute('role')) {
          el.setAttribute('role', 'link');
        }

        if (el.hasAttribute('href')) return;

        if (!el._accessibilityEnhanced) {
          const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              el.click();
            }
          };

          el.addEventListener('keydown', handleKeyDown);
          el._accessibilityEnhanced = true;
        }
      });
    };

    enhanceAccessibility();

    const observer = new MutationObserver(enhanceAccessibility);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  if (!config || !isSupertokenInitialized) return null;

  return (
    <>
      {config.name === tenantMap.juliaFisher ? (
        <SuperTokensWrapper>
          <AuthRecipeComponentsOverrideContextProvider
            components={{
              AuthPageHeader_Override: ({ DefaultComponent, ...props }) => {
                return (
                  <div>
                    <Image
                      data-supertokens="partnerLogo"
                      src={config.logo.path}
                      alt={config.name}
                      width={131}
                      height={30}
                    />
                    <Typography.Heading
                      variant="h5"
                      className="font-(--font-weight-h1)"
                    >
                      {t('WELCOME_TO_JF_CLUB')}
                    </Typography.Heading>
                    <Typography.Text
                      variant="paragraph"
                      className=" pl-8 text-start text-(length:--text-body-xl)"
                    >
                      {t('SIGN_IN_ACCESS')}
                    </Typography.Text>
                    <List.Root
                      type="unordered"
                      spacing="compact"
                      className="pl-13 pr-7"
                    >
                      <List.Item className="list-item p-0 m-0 border-0 text-start">
                        <Typography.Text variant="paragraph">
                          {t('EXCLUSIVE_VIDEO_PERFORMANCE')}
                        </Typography.Text>
                      </List.Item>
                      <List.Item className="list-item p-0 m-0 border-0 text-start">
                        <Typography.Text variant="paragraph">
                          {t('BEHIND_THE_SCENE_JULIA')}
                        </Typography.Text>
                      </List.Item>
                      <List.Item className="list-item p-0 m-0 border-0 text-start">
                        <Typography.Text variant="paragraph">
                          {t('LIVE_QA_JULIA')}
                        </Typography.Text>
                      </List.Item>
                      <List.Item className="list-item p-0 m-0 border-0 text-start">
                        <Typography.Text variant="paragraph">
                          {t('EARLY_ACCESS_CONCERT')}
                        </Typography.Text>
                      </List.Item>
                      <List.Item className="list-item p-0 m-0 border-0 text-start">
                        <Typography.Text variant="paragraph">
                          {t('PRIVATE_COMMUNITY_MUSIC')}
                        </Typography.Text>
                      </List.Item>
                    </List.Root>
                    <DefaultComponent {...props} />
                  </div>
                );
              },
            }}
          >
            <EmailPasswordComponentsOverrideProvider
              components={{
                EmailPasswordSignUpForm_Override: ({
                  DefaultComponent,
                  ...props
                }) => {
                  return (
                    <div>
                      <DefaultComponent {...props} />
                    </div>
                  );
                },
              }}
            >
              {children}
            </EmailPasswordComponentsOverrideProvider>
          </AuthRecipeComponentsOverrideContextProvider>
        </SuperTokensWrapper>
      ) : (
        <SuperTokensWrapper>
          <AuthRecipeComponentsOverrideContextProvider
            components={{
              AuthPageHeader_Override: ({
                DefaultComponent,
                isSignUp,
                ...props
              }) => {
                return (
                  <div>
                    <Image
                      data-supertokens="partnerLogo"
                      src={config.logo.path}
                      alt={config.name}
                      width={131}
                      height={30}
                    />
                    <DefaultComponent
                      isSignUp={isSignUp}
                      {...props}
                      headerLabel={
                        isSignUp
                          ? 'AUTH_CREATE_ACCOUNT'
                          : 'AUTH_PAGE_HEADER_TITLE_SIGN_IN'
                      }
                    />
                    {isSignUp ? (
                      config.configuration.tenant_id === tenantMap.BSO ? (
                        <Typography.Text data-supertokens="authHelperText">
                          {t('BSO_AUTH_LOGIN_HELPER_TEXT_PREFIX')}
                          <br />
                          {t('BSO_AUTH_LOGIN_HELPER_TEXT_SUFFIX')}
                        </Typography.Text>
                      ) : (
                        <Typography.Text data-supertokens="authHelperText">
                          {t('AUTH_SIGN_UP_DETAILS')}
                        </Typography.Text>
                      )
                    ) : config.configuration.tenant_id === tenantMap.BSO ? (
                      <Typography.Text data-supertokens="authHelperText">
                        {t('BSO_AUTH_HELPER_TEXT_PREFIX')}
                        <br />
                        {t('BSO_AUTH_HELPER_TEXT_SUFFIX')}
                      </Typography.Text>
                    ) : (
                      <Typography.Text data-supertokens="authHelperText">
                        {t('AUTH_SIGN_IN_DETAILS')}
                      </Typography.Text>
                    )}
                  </div>
                );
              },
            }}
          >
            <EmailPasswordComponentsOverrideProvider
              components={{
                EmailPasswordSignUpForm_Override: ({
                  DefaultComponent,
                  ...props
                }) => {
                  return (
                    <div>
                      <DefaultComponent {...props} />
                    </div>
                  );
                },
              }}
            >
              {children}
            </EmailPasswordComponentsOverrideProvider>
          </AuthRecipeComponentsOverrideContextProvider>
        </SuperTokensWrapper>
      )}
    </>
  );
};
